/* OTK "Need help?" button: the knight comes to life (snippets/otk-help.liquid + assets/otk-help.css).
   The page shows him as a still picture at once; when the browser is idle a small WebGL canvas is laid over it and
   moves him with the Armory hero's warp rig (assets/otk-armory.js, the `knight` shader), ported to plain WebGL 1 so no
   three.js loads on every page: he breathes, reads with small movements of the head, blinks, swings his legs in bouts
   and looks up when you reach for the button; the lamb breathes in its sleep and flicks an ear; the Bible's light
   flickers and lets a few motes drift up. The stone, its shadow, the room's tint and the wind are gone (the CSS puts a
   contact shadow on the pill). Reduced motion, no WebGL or a lost context: the picture stays. Drawing stops while the
   tab is hidden, the button has stepped aside (a drawer is open, or one of the page's own buttons is under it) or is
   off screen. */
(function () {
  'use strict';

  var root = document.querySelector('[data-otk-help]');
  if (!root || root._otkHelp) return;

  // his landmarks in the original render's space, written by scripts/make_help_knight.py: origin / size are the crop
  // of otk-help-knight.webp, skin the colour of his lids (his cheek, averaged)
  var M = /* @knight-meta (scripts/make_help_knight.py) */ {"origin":[-60,258.18],"size":[1194.0,1742.18],"skin":[0.904,0.717,0.591],"book":[150,805],"neck":[600,716],"seat":1185,"knee":[300,1345],"lambHead":[505,1350,75,115],"lambBody":[600,1100,1105,1505],"ear":[628,1243],"earBox":[618,1212,735,1300],"eyes":[[567,609,25,17],[519,613,11,16]]} /* @end */;

  // what the button must never sit on: while one of these is under it, and showing, it steps aside (fades out, as for
  // a drawer) and comes back when the page has moved on. A product's Add to cart / Buy it now (on a laptop screen they
  // reach the bottom right corner), the cart page's checkout, the homepage hero's "Shop men" / "Shop women" when the
  // hero is still (reduced motion: its walls are open from the start, so the hero's own html.otk-sakura-sides never
  // comes on, and the knight would sit on "Shop men"), and the hero's SCROLL cue (on small phones it runs under him on
  // the landing screen, until the first scroll fades it). FIND picks them out of the page; CLEAR, tested when one is
  // under the button, says whether it counts right now (the hero's buttons only while it is still: while it moves,
  // its own rule does the job, and they can sit behind its closed walls)
  var FIND = '.product-form__buttons, .cart__ctas, .otk-sakura__show-cta, .otk-sakura__hint';
  var CLEAR = '.product-form__buttons, .cart__ctas, .otk-sakura.is-static .otk-sakura__show-cta, .otk-sakura__hint';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var FRAME = 1 / 30; // his movements are slow: 30 frames a second is plenty (half the work on a 60 Hz screen)
  var GAIN = 1.4;     // he breathes a little deeper than on the Armory hero: he is half that size here

  var KV = [
    'attribute vec2 aP; varying vec2 vF;',
    'void main(){ vF = vec2(aP.x * 0.5 + 0.5, 0.5 - aP.y * 0.5); gl_Position = vec4(aP, 0.0, 1.0); }'
  ].join('\n');

  // the Armory shader minus the stone, the shadow, the room tint and the wind. vF runs down the picture like the
  // render's y; the texture is uploaded unflipped, so its rows run the same way
  var KF = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'uniform sampler2D uK; uniform vec2 uOrig, uSize, uBook, uNeck, uKnee, uEarP;',
    'uniform vec4 uLambHead, uLambBody, uEarBox, uEye0, uEye1; uniform vec3 uSkin;',
    'uniform float uSeat, uPx, uBreath, uHead, uLeg, uLamb, uEar, uBlink, uGlow, uTime;',
    'varying vec2 vF;',
    'mat2 rot(float a){ float c = cos(a), s = sin(a); return mat2(c, s, -s, c); }',
    'float ell(vec2 p, vec4 e){ vec2 d = (p - e.xy) / e.zw; return dot(d, d); }',
    'float hsh(float n){ return fract(sin(n * 12.9898) * 43758.5453); }',
    // output point -> where it comes from in the picture (small smooth displacements: one inverse step is enough)
    'vec2 warp(vec2 p){',
    '  vec2 s = p;',
    '  float wl = smoothstep(uLambBody.x, uLambBody.x + 90.0, p.x) * smoothstep(uLambBody.z + 10.0, uLambBody.z - 40.0, p.x)',
    '    * smoothstep(uLambBody.y - 30.0, uLambBody.y + 40.0, p.y) * smoothstep(uLambBody.w + 6.0, uLambBody.w - 4.0, p.y);',
    '  s.y += uLamb * 8.0 * clamp((uLambBody.w - p.y) / (uLambBody.w - uLambBody.y), 0.0, 1.0) * wl;', // the lamb's back rises
    '  vec4 eb = vec4((uEarBox.xy + uEarBox.zw) * 0.5, (uEarBox.zw - uEarBox.xy) * 0.5);',
    '  s = uEarP + rot(-uEar * smoothstep(1.35, 0.55, ell(p, eb))) * (s - uEarP);', // its ear flicks
    '  float lambA = smoothstep(uLambBody.x - 20.0, uLambBody.x + 40.0, p.x) * smoothstep(uLambBody.y - 50.0, uLambBody.y + 10.0, p.y);',
    '  s.y += uBreath * 9.0 * smoothstep(uSeat + 40.0, uSeat - 110.0, p.y) * (1.0 - lambA);', // he breathes
    '  vec2 N = uNeck - vec2(0.0, uBreath * 9.0);',
    '  s = N + rot(-uHead * smoothstep(N.y + 45.0, N.y - 45.0, p.y) * smoothstep(360.0, 440.0, p.x)) * (s - N);', // reads
    '  float wg = smoothstep(uKnee.y - 30.0, uKnee.y + 80.0, p.y) * smoothstep(600.0, 470.0, p.x) * smoothstep(1.0, 1.7, ell(p, uLambHead));',
    '  return uKnee + rot(-uLeg * wg) * (s - uKnee);', // swings his legs
    '}',
    // a lid closing over an eye: above its moving edge (curved like a lid) the eye is skin, a dark lash line rides the edge
    'float lid(vec2 s, vec4 e, out float lash, out float crease){',
    '  vec2 d = (s - e.xy) / e.zw; float inside = 1.0 - smoothstep(0.92, 1.12, length(d * vec2(0.95, 0.85)));',
    '  float edge = mix(-1.15, 0.55, uBlink) + 0.28 * (1.0 - d.x * d.x) * uBlink;',
    '  lash = exp(-pow((d.y - edge) * e.w / 1.8, 2.0)) * smoothstep(1.05, 0.7, abs(d.x)) * smoothstep(0.05, 0.35, uBlink) * step(abs(d.y), 1.3);',
    '  crease = clamp(d.y - edge + 1.0, 0.0, 1.0);',
    '  return smoothstep(edge + 0.12, edge - 0.12, d.y) * inside;',
    '}',
    'void main(){',
    '  vec2 p = uOrig + vF * uSize;',
    '  vec2 s = warp(p);',
    '  float la0, la1, cr0, cr1;',
    '  float c0 = lid(s, uEye0, la0, cr0);',
    '  float c1 = lid(s, uEye1, la1, cr1);',
    '  vec4 k = texture2D(uK, (s - uOrig) / uSize);',
    '  k = mix(k, vec4(uSkin * (0.9 + 0.1 * mix(cr1, cr0, step(c1, c0))), 1.0) * k.a, max(c0, c1));',
    '  k.rgb = mix(k.rgb, vec3(0.2, 0.11, 0.08) * k.a, max(la0, la1) * 0.85);',
    // the Bible's light (warm, flickering) on what is near it; the pages keep their own glow
    '  float dB = length((p - uBook) / vec2(1.0, 1.2));',
    '  vec3 warm = vec3(1.0, 0.8, 0.5);',
    '  vec4 c = vec4(k.rgb * mix(vec3(1.0) + warm * exp(-dB / 300.0) * uGlow * 0.2, vec3(1.0), exp(-dB / 70.0)), k.a);',
    '  float halo = exp(-dB * dB / 12000.0) * 0.3 * uGlow;', // bloom round the pages
    '  float motes = 0.0;',
    '  for (int i = 0; i < 8; i++){',
    '    float fi = float(i), T = 3.6 + 2.4 * hsh(fi + 1.0), ph = fract(uTime / T + hsh(fi + 7.0));',
    '    vec2 q = uBook + vec2(-80.0 + 160.0 * hsh(fi + 3.0) + 22.0 * sin(ph * 5.0 + fi), -30.0 - ph * 300.0);',
    '    motes += smoothstep(1.8 * uPx, 0.3 * uPx, length(p - q)) * sin(3.14159 * ph) * (0.6 + 0.4 * hsh(fi + 5.0));',
    '  }',
    '  c.rgb = min(c.rgb + warm * (halo + motes * 0.9 * uGlow), vec3(1.0));',
    // the canvas is see-through: keep the colour a valid premultiplied one, so the glow lights the page behind it
    '  c.a = max(max(c.a, (halo * 0.5 + motes * 0.8) * uGlow), max(c.r, max(c.g, c.b)));',
    '  gl_FragColor = c;',
    '}'
  ].join('\n');

  var LIVE = ['uPx', 'uBreath', 'uHead', 'uLeg', 'uLamb', 'uEar', 'uBlink', 'uGlow', 'uTime']; // set every frame

  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }

  // a damped spring, stepped in place: o[x] chases target at angular speed w with damping z
  function spring(o, x, v, target, w, z, h) {
    o[v] += (w * w * (target - o[x]) - 2 * z * w * o[v]) * h;
    o[x] += o[v] * h;
  }

  function program(gl) {
    var p = gl.createProgram();
    [[gl.VERTEX_SHADER, KV], [gl.FRAGMENT_SHADER, KF]].forEach(function (s) {
      var sh = gl.createShader(s[0]);
      gl.shaderSource(sh, s[1]);
      gl.compileShader(sh);
      gl.attachShader(p, sh);
    });
    gl.bindAttribLocation(p, 0, 'aP');
    gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }

  function Knight(el) {
    this.el = el;
    this.box = el.querySelector('.otk-help__knight');
    this.pill = el.querySelector('.otk-help__pill');
    this.img = el.querySelector('[data-otk-help-img]');
    this.t = this.raf = this.last = this.poll = 0;
    this.live = this.off = this.isAside = false;
    this.onScreen = true;
    this.things = [];
    this.aside = this.aside.bind(this);
    // the rig's state, as on the Armory hero
    this.st = { hover: false, head: 0, headV: 0, leg: 0, legV: 0, bout: 3, boutOn: false, blinkT: 2.5, blinkP: -1, dbl: false,
      ear: 0, earV: 0, earT: 9, glow: 1, glowKick: 0 };
    this.v = { uBreath: 0, uHead: 0, uLeg: 0, uLamb: 0, uEar: 0, uBlink: 0, uGlow: 1, uTime: 0, uPx: 16 };
    this.tick = this.tick.bind(this);
    var self = this, el2 = el, check = function () { self.check(); }, wake = function () { self.wake(); };
    // reaching for him: a mouse or pen over the pill or his outline, or keyboard focus (a tap just opens the link)
    el.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'touch') self.reach(true); });
    el.addEventListener('pointerleave', function (e) { if (e.pointerType !== 'touch') self.reach(false); });
    el.addEventListener('focus', function () {
      var kb = true;
      try { kb = el2.matches(':focus-visible'); } catch (e) { /* older browsers: any focus */ }
      if (kb) self.reach(true);
      self.check(); // keyboard focus keeps it from stepping aside (otk-help.css)
    });
    el.addEventListener('blur', function () { self.reach(false); self.check(); });
    document.addEventListener('visibilitychange', wake);
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (en) { self.onScreen = en[en.length - 1].isIntersecting; self.wake(); }).observe(el);
    }
    // the button steps aside (otk-help.css sets --otk-help-off) when Dawn opens a drawer or modal, or the hero opens
    // its side walls: both are class changes on <body> / <html>
    if (window.MutationObserver) {
      var mo = new MutationObserver(check), only = { attributes: true, attributeFilter: ['class'] };
      mo.observe(document.documentElement, only);
      mo.observe(document.body, only);
    }
    // a new screen size moves the button (and the phone sizes change it): measure its footprint again once it settles
    var rewatch = 0, settle = 0;
    window.addEventListener('resize', function () {
      if (self.live) self.fit();
      clearTimeout(rewatch);
      rewatch = setTimeout(function () { self.watch(); }, 200);
    });
    // a product's buttons are built anew when its variant changes (Dawn swaps the product's HTML): once a scroll
    // settles, look for the things to keep clear of again, and watch the new ones (a cheap query, no layout)
    window.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () { if (self.stale()) self.watch(); }, 150);
    }, { passive: true });
    var motion = function () { if (REDUCED.matches) self.stop(); else self.start(); };
    if (REDUCED.addEventListener) REDUCED.addEventListener('change', motion);
    else if (REDUCED.addListener) REDUCED.addListener(motion);
    this.check();
    this.watch();
  }

  // ---- stepping aside for the page's own buttons (FIND / CLEAR above)

  // the button's footprint: the pill, and the knight's box less its empty left margin (his feet and the Bible's glow
  // start ~10% in, see --kn-hit in the CSS). The corner above the pill's right end is empty, so it isn't part of it
  Knight.prototype.zones = function () {
    var p = this.pill.getBoundingClientRect(), k = this.box.getBoundingClientRect(), cut = k.width * 0.08;
    return [{ l: p.left, t: p.top, r: p.right, b: p.bottom }, { l: k.left + cut, t: k.top, r: k.right, b: k.bottom }];
  };

  // An IntersectionObserver whose root is shrunk (rootMargin) to the corner of the screen the button sits in, from
  // the top of his helmet and the left end of the pill down to the screen's bottom right corner, reports whenever one
  // of the things moves into that corner or out of it, however it moves: the page scrolling, the hero's animation, an
  // image above pushing the product form down. No scroll handler does that work. aside() then tests the exact
  // footprint (so the empty strip round the button only makes it look closer). Rebuilt when the button moves (a
  // resize, Shopify's preview bar). In a frame from another site (the theme editor) the browser ignores rootMargin and
  // reports the whole screen: the exact test, repeated while anything is reported, covers that too.
  Knight.prototype.watch = function () {
    var self = this, z = this.zone = this.zones();
    if (this.io) this.io.disconnect();
    this.io = null;
    this.things = [].slice.call(document.querySelectorAll(FIND));
    this.hit = this.things.map(function () { return true; }); // until the observer first reports: check them all
    if (window.IntersectionObserver && this.things.length) {
      var m = -Math.max(0, Math.floor(Math.min(z[0].t, z[1].t))) + 'px 0px 0px ' + -Math.max(0, Math.floor(Math.min(z[0].l, z[1].l))) + 'px';
      this.io = new IntersectionObserver(function (en) {
        var gone = false;
        en.forEach(function (e) {
          var j = self.things.indexOf(e.target);
          if (j >= 0) self.hit[j] = e.isIntersecting;
          if (!e.target.isConnected) gone = true;
        });
        if (gone) setTimeout(function () { self.watch(); }, 60); // taken out of the page (a new variant): watch its successor
        else self.aside();
      }, { rootMargin: m });
      this.things.forEach(function (t) { self.io.observe(t); });
    }
    this.aside(); // at once, so a button that starts under it never shows it
  };

  Knight.prototype.stale = function () {
    var now = document.querySelectorAll(FIND), i;
    if (now.length !== this.things.length) return true;
    for (i = 0; i < now.length; i++) if (now[i] !== this.things[i]) return true;
    return false;
  };

  // steps aside while a thing that counts (CLEAR) and is showing overlaps the footprint. While anything is reported in
  // the button's corner, this runs again four times a second: the hero's SCROLL cue fades out with the first scroll
  // without moving, which no observer reports
  Knight.prototype.aside = function () {
    var z = this.zone, on = false, near = false, j, i, t, r, cs;
    for (j = 0; j < this.things.length && !on; j++) {
      if (!this.hit[j]) continue;
      near = true;
      t = this.things[j];
      if (!t.matches(CLEAR)) continue;
      cs = getComputedStyle(t);
      if (cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) continue;
      r = t.getBoundingClientRect();
      for (i = 0; i < z.length; i++) if (r.right > z[i].l && r.left < z[i].r && r.bottom > z[i].t && r.top < z[i].b) on = true;
    }
    if (near && !this.poll) this.poll = setInterval(this.aside, 250);
    else if (!near && this.poll) this.poll = clearInterval(this.poll) || 0;
    if (on === this.isAside) return;
    this.isAside = on;
    this.el.classList.toggle('is-aside', on);
    this.check();
  };

  Knight.prototype.reach = function (on) {
    if (on && !this.st.hover) {
      this.st.earV += 6;                                   // the lamb's ear twitches
      this.st.glowKick = Math.max(this.st.glowKick, 0.35); // and the Bible flares
    }
    this.st.hover = on;
  };

  Knight.prototype.check = function () {
    this.off = getComputedStyle(this.el).getPropertyValue('--otk-help-off').trim() === '1';
    this.wake();
  };

  // build the canvas once the page is idle; the picture stays until its first frame is on screen
  Knight.prototype.start = function () {
    if (this.failed || REDUCED.matches || !M) return;
    if (this.gl) return this.ok && this.resume(); // motion allowed again
    var self = this, canvas = document.createElement('canvas'), gl = null;
    var opts = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' };
    try { gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts); } catch (e) { gl = null; }
    var prog = gl && program(gl);
    if (!prog) return this.fail();
    this.canvas = canvas;
    this.gl = gl;
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // one triangle over it all
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    var L = this.loc = {}, at = function (n) { return gl.getUniformLocation(prog, n); }, k;
    var fixed = { uOrig: M.origin, uSize: M.size, uBook: M.book, uNeck: M.neck, uKnee: M.knee, uEarP: M.ear, uSkin: M.skin,
      uLambHead: M.lambHead, uLambBody: M.lambBody, uEarBox: M.earBox, uEye0: M.eyes[0], uEye1: M.eyes[1] };
    for (k in fixed) gl['uniform' + fixed[k].length + 'fv'](at(k), fixed[k]); // the landmarks never change
    gl.uniform1f(at('uSeat'), M.seat);
    LIVE.forEach(function (n) { L[n] = at(n); });
    // the same picture the page shows (from the browser cache), asked for with CORS so WebGL may read it
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () {
      try {
        var T = gl.TEXTURE_2D;
        gl.bindTexture(T, gl.createTexture());
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
        gl.texImage2D(T, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, im);
        // not a power of two: no mipmaps, clamped edges
        [[gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR], [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE]]
          .forEach(function (p) { gl.texParameteri(T, p[0], p[1]); });
      } catch (e) {
        return self.fail(); // e.g. a picture the CDN served without CORS
      }
      self.ready();
    };
    im.onerror = function () { self.fail(); };
    im.src = this.img.currentSrc || this.img.src;
  };

  Knight.prototype.ready = function () {
    var self = this, c = this.canvas;
    this.ok = true;
    c.setAttribute('aria-hidden', 'true');
    this.box.insertBefore(c, this.img.nextSibling); // over the picture, under his hit outline and the speech bubble
    c.addEventListener('webglcontextlost', function () { self.fail(); });
    this.fit();
    this.frame(0);
    this.draw();
    // show the canvas once that frame has reached the screen (the picture under it hides after the fade, see the CSS)
    requestAnimationFrame(function () { if (!REDUCED.matches) self.resume(); });
  };

  Knight.prototype.resume = function () {
    if (this.failed) return;
    this.live = true;
    this.el.classList.add('is-live');
    this.fit();
    this.wake();
  };

  Knight.prototype.stop = function () {
    this.live = false;
    this.el.classList.remove('is-live');
    this.wake();
  };

  Knight.prototype.fail = function () {
    this.failed = true;
    this.stop();
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  };

  // canvas pixels = its CSS size x the screen's pixel ratio (at most 2)
  Knight.prototype.fit = function () {
    var w = this.box.offsetWidth, h = this.box.offsetHeight, r = Math.min(window.devicePixelRatio || 1, 2), c = this.canvas;
    if (!w || !h || !this.gl) return;
    this.v.uPx = M.size[1] / h * (r > 1.5 ? 1 : 1.2); // render units per CSS px: the motes stay ~2 px across
    if (c.width === Math.round(w * r) && c.height === Math.round(h * r)) return;
    c.width = Math.round(w * r);
    c.height = Math.round(h * r);
    this.gl.viewport(0, 0, c.width, c.height);
    if (!this.raf) this.draw();
  };

  Knight.prototype.wake = function () {
    var run = this.live && !document.hidden && this.onScreen && !this.off;
    if (run && !this.raf) {
      this.last = 0;
      this.raf = requestAnimationFrame(this.tick);
    } else if (!run && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  };

  Knight.prototype.tick = function (now) {
    this.raf = requestAnimationFrame(this.tick);
    if (this.last && now - this.last < 1000 * FRAME - 4) return;
    var dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : FRAME;
    this.last = now;
    this.frame(dt);
    this.draw();
  };

  // the rig, as on the Armory hero (otk-armory.js knight.frame), without the room's light and the wind
  Knight.prototype.frame = function (dt) {
    var st = this.st, v = this.v, t = (this.t += dt), n = Math.max(1, Math.ceil(dt * 240)), h = dt / n, i;
    // breathing: in slowly, out a little quicker; the lamb slower and deeper, asleep
    var br = 0.5 - 0.5 * Math.cos(2 * Math.PI * t / 4.2);
    v.uBreath = GAIN * br * br * (3 - 2 * br);
    v.uLamb = GAIN * (0.5 - 0.5 * Math.cos(2 * Math.PI * (t + 1.3) / 3.3));
    // the head: small reading movements, a look up when you reach for him
    var read = 0.016 * Math.sin(t * 0.43) + 0.009 * Math.sin(t * 1.21 + 1) + 0.006 * Math.sin(t * 2.7 + 0.4) * (0.5 + 0.5 * Math.sin(t * 0.17));
    for (i = 0; i < n; i++) spring(st, 'head', 'headV', st.hover ? 0.13 : read, 5.5, 0.75, h);
    v.uHead = st.head;
    // the legs: swinging in bouts, like a boy on a high seat (a pendulum driven at its own pace, then left to settle)
    if ((st.bout -= dt) <= 0) {
      st.boutOn = !st.boutOn;
      st.bout = st.boutOn ? 5 + Math.random() * 6 : 3 + Math.random() * 5;
    }
    var w0 = 2 * Math.PI / 1.45, drive = (st.boutOn ? 0.55 : 0) * Math.sin(t * w0);
    for (i = 0; i < n; i++) {
      st.legV += (-w0 * w0 * st.leg - 2 * 0.09 * w0 * st.legV + drive) * h;
      st.leg += st.legV * h;
    }
    v.uLeg = clamp(st.leg, -0.09, 0.09);
    // blinking, now and then twice
    if ((st.blinkT -= dt) <= 0 && st.blinkP < 0) {
      st.blinkP = 0;
      st.dbl = Math.random() < 0.2;
      st.blinkT = 2.4 + Math.random() * 4;
    }
    if (st.blinkP >= 0) {
      var b = (st.blinkP += dt);
      v.uBlink = b < 0.07 ? b / 0.07 : b < 0.11 ? 1 : Math.max(0, 1 - (b - 0.11) / 0.12);
      if (b > 0.24) {
        st.blinkP = -1;
        v.uBlink = 0;
        if (st.dbl) { st.dbl = false; st.blinkT = 0.12; }
      }
    }
    // the lamb's ear: a quick flick every so often
    if ((st.earT -= dt) <= 0) {
      st.earV += 8 + Math.random() * 6;
      st.earT = 7 + Math.random() * 9;
    }
    for (i = 0; i < n; i++) spring(st, 'ear', 'earV', 0, 2 * Math.PI / 0.32, 0.22, h);
    v.uEar = clamp(st.ear, -0.35, 0.35);
    // the Bible's light: a slow candle-like flicker, brighter while you reach for him
    st.glowKick = Math.max(0, st.glowKick - dt * 0.5);
    var fl = 0.9 + 0.06 * Math.sin(t * 2.3) + 0.04 * Math.sin(t * 5.7 + 1.1) + 0.03 * Math.sin(t * 11.3);
    st.glow += (fl + (st.hover ? 0.25 : 0) + st.glowKick - st.glow) * Math.min(1, dt * 4);
    v.uGlow = st.glow;
    v.uTime = t % 3600;
  };

  Knight.prototype.draw = function () {
    var gl = this.gl, L = this.loc, v = this.v;
    if (!gl || gl.isContextLost()) return;
    LIVE.forEach(function (n) { gl.uniform1f(L[n], v[n]); });
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  // theme previews only: Shopify's preview bar covers the bottom of the screen, so sit above it (the Inbox bubble did).
  // True when that moved the button
  function previewBar() {
    var bar = document.getElementById('PBarNextFrame'), lift = bar && bar.offsetHeight ? '68px' : '0px';
    if (root.style.getPropertyValue('--otk-help-lift') === lift) return false;
    root.style.setProperty('--otk-help-lift', lift);
    return true;
  }
  previewBar();

  var knight = (root._otkHelp = new Knight(root));
  var moved = function () { if (previewBar()) knight.watch(); };

  function later() {
    moved();
    setTimeout(moved, 2500);
    if (window.requestIdleCallback) window.requestIdleCallback(function () { knight.start(); }, { timeout: 2500 });
    else setTimeout(function () { knight.start(); }, 600);
  }

  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later);
})();
